import type { ReactNode } from 'react'

import { CircleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'

interface FieldProps {
    id: string
    label: ReactNode
    error?: string
    hint?: ReactNode
    optional?: boolean
    className?: string
    children: ReactNode
    labelAction?: ReactNode
}

/** Label, control, hint and error message, wired together by id. */
function Field({ id, label, error, hint, optional = false, className, children, labelAction }: FieldProps) {
    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <div className='flex items-center justify-between gap-3'>
                <Label htmlFor={id}>
                    {label}

                    {optional && (
                        <span className='font-normal text-muted-foreground'>
                            (opsional)
                        </span>
                    )}
                </Label>

                {labelAction}
            </div>

            {children}

            {error
                ? (
                    <p id={`${id}-error`} role='alert' className='flex items-start gap-1.5 text-[0.8125rem] font-medium text-destructive'>
                        <CircleAlert aria-hidden='true' className='mt-px size-4 shrink-0' />
                        {error}
                    </p>
                )
                : hint && (
                    <p id={`${id}-hint`} className='text-[0.8125rem] text-muted-foreground'>
                        {hint}
                    </p>
                )}
        </div>
    )
}

/** aria props for a control rendered inside <Field>. */
const fieldAria = (id: string, error?: string, hasHint = false) => ({
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? `${id}-error` : hasHint ? `${id}-hint` : undefined
})

export { Field, fieldAria }
